import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  increment,
  limit as fbLimit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter as fbStartAfter,
  Timestamp,
  updateDoc,
  WhereFilterOp,
  where
} from 'firebase/firestore';
import { runTransaction } from 'firebase/firestore';
import db from '../utils/firestore';
import { getAuth } from 'firebase/auth';
import { logCrossUidAttempt } from '../lib/logging/secureLogger';

export type PaginationOptions = {
  limit?: number;
  startAfter?: DocumentSnapshot | null;
};

export type Page<T> = {
  items: T[];
  nextCursor: DocumentSnapshot | null;
};

export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConflictError';
  }
}

// Exponential backoff with jitter for transient Firestore errors
export async function withBackoff<T>(operation: () => Promise<T>, options?: {
  maxRetries?: number;
  baseDelayMs?: number; // default 250ms
  maxDelayMs?: number; // default 8000ms
}): Promise<T> {
  const maxRetries = options?.maxRetries ?? 5;
  const base = options?.baseDelayMs ?? 250;
  const max = options?.maxDelayMs ?? 8000;

  let attempt = 0;
  while (true) {
    try {
      return await operation();
    } catch (err: any) {
      const code: string | undefined = err?.code;
      const transient = code === 'aborted' || code === 'deadline-exceeded' || code === 'unavailable' || code === 'resource-exhausted' || code === 'internal';
      if (!transient || attempt >= maxRetries) throw err;
      const delay = Math.min(max, base * Math.pow(2, attempt));
      const jitter = delay * (0.2 * (Math.random() - 0.5));
      await new Promise((r) => setTimeout(r, Math.max(0, delay + jitter)));
      attempt += 1;
    }
  }
}

// Debounce manager for per-document/per-user writes with flush/cancel.
//
// INCIDENT-20260822 (the vanished translation): schedule() used to be void and
// swallow the eventual setDoc rejection in a generic console.error — so
// debouncedSetDoc's returned promise resolved at SCHEDULING time and the loud
// "NEVER silent" error handler in upsertThreadMessages (added 2026-08-20 for
// exactly this incident class) was dead code. A Firestore invalid-argument
// rejection (oversized doc) was invisible to every caller; the thread's server
// copy silently froze. schedule() now keeps a per-key deferred settled by the
// ACTUAL write outcome — superseding schedules for the same key share it
// (same logical write; latest data wins) — so callers' awaits, retries and
// catch blocks are live again, uniformly, for every debounced write.
class DebounceManager {
  private timers: Map<string, number> = new Map();
  private queued: Map<string, () => Promise<any>> = new Map();
  private deferred: Map<string, { promise: Promise<void>; resolve: () => void; reject: (e: unknown) => void }> = new Map();

  private deferredFor(key: string) {
    let d = this.deferred.get(key);
    if (!d) {
      let resolve!: () => void;
      let reject!: (e: unknown) => void;
      const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
      // A caller may schedule fire-and-forget and never await; an eventual
      // rejection must not become an unhandled-rejection crash for THEM.
      promise.catch(() => {});
      d = { promise, resolve, reject };
      this.deferred.set(key, d);
    }
    return d;
  }

  private async run(key: string, task: () => Promise<any>): Promise<void> {
    const d = this.deferredFor(key);
    this.deferred.delete(key);
    try {
      await withBackoff(task);
      d.resolve();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Debounced write failed:', e);
      d.reject(e);
    }
  }

  schedule(key: string, fn: () => Promise<any>, delayMs: number): Promise<void> {
    const handle0 = this.timers.get(key);
    if (handle0) {
      window.clearTimeout(handle0);
      this.timers.delete(key);
    }
    this.queued.set(key, fn);
    const d = this.deferredFor(key);
    const handle = window.setTimeout(() => {
      const task = this.queued.get(key);
      this.queued.delete(key);
      this.timers.delete(key);
      if (task) void this.run(key, task);
    }, delayMs);
    this.timers.set(key, handle);
    return d.promise;
  }

  flush(key: string): Promise<void> {
    const task = this.queued.get(key);
    if (!task) return this.deferred.get(key)?.promise ?? Promise.resolve();
    const handle = this.timers.get(key);
    if (handle) {
      window.clearTimeout(handle);
      this.timers.delete(key);
    }
    this.queued.delete(key);
    return this.run(key, task);
  }

  cancel(key: string): void {
    const handle = this.timers.get(key);
    if (handle) {
      window.clearTimeout(handle);
      this.timers.delete(key);
    }
    this.queued.delete(key);
    // A deliberate cancel means "this write no longer matters": settle any
    // waiter as resolved rather than leaving it pending forever.
    const d = this.deferred.get(key);
    if (d) {
      this.deferred.delete(key);
      d.resolve();
    }
  }

  flushAll(): Promise<void> {
    // Settled, not all: one failed flush must not hide the others' outcomes
    // from a caller that awaits (sign-out uses this).
    return Promise.allSettled(Array.from(this.queued.keys()).map((k) => this.flush(k))).then(() => undefined);
  }

  hasPending(key?: string): boolean {
    if (key) return this.queued.has(key);
    return this.queued.size > 0;
  }

  getPendingKeys(): string[] {
    return Array.from(this.queued.keys());
  }
}

export const debounceManager = new DebounceManager();

export function installDebounceAutoFlush(): void {
  if (typeof window === 'undefined') return;
  // Prevent duplicate listener installation
  const flagKey = '__debounce_flush_installed__';
  const anyWindow: any = window as any;
  if (anyWindow[flagKey]) return;
  anyWindow[flagKey] = true;
  window.addEventListener('beforeunload', () => debounceManager.flushAll());
  window.addEventListener('blur', () => debounceManager.flushAll());
  window.addEventListener('pagehide', () => debounceManager.flushAll());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') debounceManager.flushAll();
  });
}

export const Paths = {
  userDoc: (uid: string) => doc(db, 'users', uid),
  // User profile stored as an array-of-map in subcollection user_profile
  // Document path: users/{uid}/user_profile/user_profile with array field profiles
  userProfileDoc: (uid: string) => doc(db, 'users', uid, 'user_profile', 'user_profile'),
  // General configuration for a user stored as an array-of-map in subcollection user_general
  // Document path: users/{uid}/user_general/user_general
  userConfigGeneral: (uid: string) => doc(db, 'users', uid, 'user_general', 'user_general'),
  // Examples stored as an array-of-map in subcollection user_examples
  // Document path: users/{uid}/user_examples/user_examples with array field examples
  userConfigExamples: (uid: string) => doc(db, 'users', uid, 'user_examples', 'user_examples'),
  // Autonomy profiles stored as an array-of-map in subcollection user_autonomyprofiles
  // Document path: users/{uid}/user_autonomyprofiles/user_autonomyprofiles with array field autonomy_profiles
  userConfigAutonomyProfiles: (uid: string) => doc(db, 'users', uid, 'user_autonomyprofiles', 'user_autonomyprofiles'),
  // Monetization configuration for a user stored as an array in subcollection user_monetization
  // Document path: users/{uid}/user_monetization/user_monetization and field user_monetization: MonetizationDoc[]
  userConfigMonetization: (uid: string) => doc(db, 'users', uid, 'user_monetization', 'user_monetization'),
  // Behaviors configuration for a user stored as an array in subcollection user_behaviors
  // Document path: users/{uid}/user_behaviors/user_behaviors and field user_behaviors: BehaviorDoc[]
  userConfigBehaviors: (uid: string) => doc(db, 'users', uid, 'user_behaviors', 'user_behaviors'),
  // Members configuration for a user stored as an array in subcollection user_member
  // Document path: users/{uid}/user_member/user_members and field members: MemberDoc[]
  userConfigMembers: (uid: string) => doc(db, 'users', uid, 'user_member', 'user_members'),
  // Positions configuration for a user stored as an array in subcollection user_positions
  // Document path: users/{uid}/user_positions/user_positions and field positions: PositionDoc[]
  userConfigPositions: (uid: string) => doc(db, 'users', uid, 'user_positions', 'user_positions'),
  // Backward compatibility: roles path
  userConfigRoles: (uid: string) => doc(db, 'users', uid, 'user_roles', 'user_roles'),
  // Accounts removed from data model
  // Assets configuration for a user stored as an array in subcollection user_assets
  // Document path: users/{uid}/user_assets/user_assets and field assets: AssetDoc[]
  userConfigAssets: (uid: string) => doc(db, 'users', uid, 'user_assets', 'user_assets'),
  // Policies configuration for a user stored as an array in subcollection user_policies
  // Document path: users/{uid}/user_policies/user_policies and field policies: PolicyDoc[]
  userConfigPolicies: (uid: string) => doc(db, 'users', uid, 'user_policies', 'user_policies'),
  // Procedures configuration for a user stored as an array in subcollection user_procedures
  // Document path: users/{uid}/user_procedures/user_procedures and field procedures: ProcedureDoc[]
  userConfigProcedures: (uid: string) => doc(db, 'users', uid, 'user_procedures', 'user_procedures'),
  // Tools configuration stored as an array on a single document
  // Document path: users/{uid}/user_tools/user_tools and field tools: ToolDoc[]
  userConfigTools: (uid: string) => doc(db, 'users', uid, 'user_tools', 'user_tools'),
  // Triggers configuration stored as an array on a single document
  // Document path: users/{uid}/user_triggers/user_triggers and field triggers: TriggerDoc[]
  userConfigTriggers: (uid: string) => doc(db, 'users', uid, 'user_triggers', 'user_triggers'),
  configCol: (uid: string, subcollection: string) => {
    if (subcollection === 'memberships') throw new Error('Forbidden collection name: memberships');
    return collection(db, 'users', uid, 'config', subcollection);
  },
  configDoc: (uid: string, subcollection: string, id: string) => {
    if (subcollection === 'memberships') throw new Error('Forbidden collection name: memberships');
    return doc(db, 'users', uid, 'config', subcollection, id);
  },
  userMonetizationPlansCol: (uid: string) => collection(db, 'users', uid, 'monetization_plans'),
  userMonetizationPlanDoc: (uid: string, id: string) => doc(db, 'users', uid, 'monetization_plans', id),
  // SAVED PROMPTS (2026-09-17). Written as a slash PATH, not as segments, and
  // that is load-bearing rather than a style choice: the data-rights scanner
  // (scripts/lib/user-data-paths.mjs `collectionsIn`) finds every collection
  // customer data lands in by matching the literal template form
  // `users/${x}/<collection>` in source text. A segment-built path is invisible
  // to it, so writing these the tidy way would make config/user-data-paths.json's
  // `prompt_templates` classification read as stale and turn
  // scripts/gates.d/user-data-paths.gate red — for a feature that is MORE
  // compliant than what it replaced. Do not "tidy" these into Paths.domainCol.
  promptTemplatesCol: (uid: string) => collection(db, `users/${uid}/prompt_templates`),
  promptTemplateDoc: (uid: string, id: string) => doc(db, `users/${uid}/prompt_templates/${id}`),
  // The workspace's SHARED library (phase 2). Same literal-path rule as above,
  // for the same scanner. READ-ONLY from a browser by construction:
  // firestore.rules allows `read` here for a verified member holding
  // prompts.library.view and denies every client write outright, so this
  // collection has no `…Doc` sibling — a write goes through the cores in
  // functions/src/promptLibrary, which is what lets one ladder judge it
  // whether it arrives from this panel or from Prest0n.
  sharedPromptsCol: (accountId: string) => collection(db, `accounts/${accountId}/prompt_library`),
  // SCHEDULED MESSAGES (2026-09-24, "send later"). Same literal-path rule, for
  // the same scanner. READ-ONLY from a browser: firestore.rules gives the owner
  // get and list and nobody any write, so there is no `…Doc` sibling — every
  // write goes through functions/src/scheduledMessages/scheduledMessageCores.ts.
  scheduledMessagesCol: (uid: string) => collection(db, `users/${uid}/scheduled_messages`),
  domainCol: (uid: string, domain: string) => {
    if (domain === 'memberships') throw new Error('Forbidden collection name: memberships');
    return collection(db, 'users', uid, domain);
  },
  domainDoc: (uid: string, domain: string, id: string) => {
    if (domain === 'memberships') throw new Error('Forbidden collection name: memberships');
    return doc(db, 'users', uid, domain, id);
  },
  triggerElementsCol: (uid: string, triggerId: string) => collection(db, 'users', uid, 'triggers', triggerId, 'trigger_elements'),
  triggerElementDoc: (uid: string, triggerId: string, elementId: string) => doc(db, 'users', uid, 'triggers', triggerId, 'trigger_elements', elementId),
  // Phase 3 — per-message chat docs. Subcollection under the thread INDEX doc
  // (users/{uid}/chat_threads/{threadId}); one doc per message keyed by
  // messageId, coexisting with the legacy whole-array
  // users/{uid}/chat_thread_messages/{threadId} doc during migration.
  chatThreadMessageDocsCol: (uid: string, threadId: string) => collection(db, 'users', uid, 'chat_threads', threadId, 'messages'),
  chatThreadMessageDoc: (uid: string, threadId: string, messageId: string) => doc(db, 'users', uid, 'chat_threads', threadId, 'messages', messageId),
  modelParamsCol: (uid: string, modelId: string) => collection(db, 'users', uid, 'models', modelId, 'custom_parameters'),
  modelParamDoc: (uid: string, modelId: string, paramId: string) => doc(db, 'users', uid, 'models', modelId, 'custom_parameters', paramId),
  // Tool elements are stored inline within users/{uid}/user_tools/user_tools.tools[].tool_elements
  profileSettingsDoc: (uid: string) => doc(db, 'users', uid, 'profile', 'profile_settings'),
  apiKeysDoc: (uid: string) => doc(db, 'users', uid, 'profile', 'api_keys'), // DO NOT INDEX fields in indexes.json
  // User settings per spec: users/{uid}/user_settings/user_settings
  userSettingsDoc: (uid: string) => doc(db, 'users', uid, 'user_settings', 'user_settings'),
  userWalletPageDoc: (uid: string) => doc(db, 'users', uid, 'user_wallet_page', 'user_wallet_page'),
  userProfileSettingsDoc: (uid: string) => doc(db, 'users', uid, 'user_profile_settings', 'profile_settings'),
  userProfileNotificationPreferencesDoc: (uid: string) => doc(db, 'users', uid, 'user_profile_settings', 'notification_preferences'),
  // Device tokens for FCM push notifications
  userDeviceTokensCol: (uid: string) => collection(db, 'users', uid, 'device_tokens'),
  userDeviceTokenDoc: (uid: string, token: string) => doc(db, 'users', uid, 'device_tokens', token),
  // Per-channel delivery outbox (processed by Cloud Functions)
  userMessageOutboxCol: (uid: string) => collection(db, 'users', uid, 'message_outbox'),
  userMessageOutboxDoc: (uid: string, jobId: string) => doc(db, 'users', uid, 'message_outbox', jobId),
  // Action interactions & requests
  userActionEventsCol: (uid: string) => collection(db, 'users', uid, 'message_action_events'),
  userActionEventDoc: (uid: string, id: string) => doc(db, 'users', uid, 'message_action_events', id),
  userActionRequestsCol: (uid: string) => collection(db, 'users', uid, 'message_action_requests'),
  userActionRequestDoc: (uid: string, id: string) => doc(db, 'users', uid, 'message_action_requests', id),
  // In-app platform messages (real-time, per-user)
  // Note: Firestore requires collection/doc alternation; we implement messages as docs under collection 'platform_messages'
  // Path per doc: users/{uid}/platform_messages/{message_id}
  userPlatformMessagesCol: (uid: string) => collection(db, 'users', uid, 'platform_messages'),
  userPlatformMessageDoc: (uid: string, messageId: string) => doc(db, 'users', uid, 'platform_messages', messageId),
  userPlatformMessageEventsCol: (uid: string, messageId: string) => collection(db, 'users', uid, 'platform_messages', messageId, 'events'),
  userPlatformMessageEventDoc: (uid: string, messageId: string, eventId: string) => doc(db, 'users', uid, 'platform_messages', messageId, 'events', eventId),
  userNotificationsCol: (uid: string) => collection(db, 'users', uid, 'user_notifications'),
  userNotificationDoc: (uid: string, id: string) => doc(db, 'users', uid, 'user_notifications', id),
  // Widget reports saved to owner account: users/{uid}/widget_reports/{reportId}
  widgetReportsCol: (uid: string) => collection(db, 'users', uid, 'widget_reports'),
  widgetReportDoc: (uid: string, reportId: string) => doc(db, 'users', uid, 'widget_reports', reportId),
  // Legacy wallet doc (payment methods): users/{uid}/user_wallet/user_wallet
  userWalletDoc: (uid: string) => doc(db, 'users', uid, 'user_wallet', 'user_wallet'),
  // Consolidated wallet structure: users/{uid}/wallet/...
  // Summary document with balance, totals (4 segments = document)
  walletSummaryDoc: (uid: string) => doc(db, 'users', uid, 'wallet', 'summary'),
  // Wallet earnings: users/{uid}/wallet/summary/earnings/{earningId} (5 segments = collection)
  walletEarningsCol: (uid: string) => collection(db, 'users', uid, 'wallet', 'summary', 'earnings'),
  walletEarningDoc: (uid: string, id: string) => doc(db, 'users', uid, 'wallet', 'summary', 'earnings', id),
  // Wallet expenses: users/{uid}/wallet/summary/expenses/{expenseId} (5 segments = collection)
  walletExpensesCol: (uid: string) => collection(db, 'users', uid, 'wallet', 'summary', 'expenses'),
  walletExpenseDoc: (uid: string, id: string) => doc(db, 'users', uid, 'wallet', 'summary', 'expenses', id),
  // Workspace wallet ledger (R19, 2026-08-18): accounts with
  // walletMode:'workspace' debit accounts/{id}/wallet/summary/expenses —
  // the wallet page reads THIS ledger for them, or task debits are
  // invisible. Rules: verified account members read, server writes.
  walletExpensesColForAccount: (accountId: string) =>
    collection(db, 'accounts', accountId, 'wallet', 'summary', 'expenses'),
  walletDisplaySummaryDoc: (accountId: string) =>
    doc(db, 'accounts', accountId, 'wallet', 'displaySummary'),
  // Wallet usage: users/{uid}/wallet/summary/usage/{usageId} (5 segments = collection)
  walletUsageCol: (uid: string) => collection(db, 'users', uid, 'wallet', 'summary', 'usage'),
  walletUsageDoc: (uid: string, id: string) => doc(db, 'users', uid, 'wallet', 'summary', 'usage', id),
  // Wallet transactions: users/{uid}/wallet/summary/transactions/{transactionId} (5 segments = collection)
  walletTransactionsCol: (uid: string) => collection(db, 'users', uid, 'wallet', 'summary', 'transactions'),
  walletTransactionDoc: (uid: string, id: string) => doc(db, 'users', uid, 'wallet', 'summary', 'transactions', id),
  historyConversationsCol: (uid: string) => collection(db, 'users', uid, 'history', 'conversations'),
  conversationDoc: (uid: string, conversationId: string) => doc(db, 'users', uid, 'history', 'conversations', conversationId),
  conversationMessagesCol: (uid: string, conversationId: string) => collection(db, 'users', uid, 'history', 'conversations', conversationId, 'messages'),
  // Conversation history: users/{uid}/user_history/user_history
  userConversationHistoryDoc: (uid: string) => doc(db, 'users', uid, 'user_history', 'user_history'),
  // Namespaced conversation history for tenants/widgets: users/{uid}/{namespace}/{namespace}
  // Used by widget tenants to store conversations in separate collections
  tenantConversationHistoryDoc: (uid: string, namespace: string) => doc(db, 'users', uid, namespace, namespace),
  // Sequence history (user_history/sequence_history) — normalized helpers
  sequenceHistoryRootDoc: (uid: string) => doc(db, 'users', uid, 'user_history', 'sequence_history'),
  sequenceHistorySequencesCol: (uid: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences'),
  sequenceHistorySequenceDoc: (uid: string, sequenceId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId),
  sequenceHistoryStepsCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'steps'),
  sequenceHistoryStepDoc: (uid: string, sequenceId: string, stepId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'steps', stepId),
  sequenceHistoryBranchesCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'branches'),
  sequenceHistoryBranchDoc: (uid: string, sequenceId: string, branchId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'branches', branchId),
  // Legacy compatibility: users/{uid}/user_history/sequence_history/{sequence_id}/{branch_id}
  sequenceHistoryLegacyBranchDoc: (uid: string, sequenceId: string, branchId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', sequenceId, branchId),
  userHistorySequenceLegacyDoc: (uid: string, sequenceId: string) => doc(db, 'users', uid, 'user_history', sequenceId),
  sequenceHistoryTransactionsCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'transactions'),
  sequenceHistoryTransactionDoc: (uid: string, sequenceId: string, txId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'transactions', txId),
  sequenceHistoryAlertsCol: (uid: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'alerts'),
  sequenceHistoryAlertDoc: (uid: string, alertId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'alerts', alertId),
  sequenceHistoryDailyCostDoc: (uid: string, dateKey: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'costs_daily', dateKey),
  sequenceHistoryToolRunsCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'tool_runs'),
  sequenceHistoryToolRunDoc: (uid: string, sequenceId: string, runId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'tool_runs', runId),
  sequenceHistoryExecutionControlDoc: (uid: string, sequenceId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'execution_control', 'execution_control'),
  sequenceHistoryBackupsCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'backups'),
  sequenceHistoryBackupDoc: (uid: string, sequenceId: string, backupId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'backups', backupId),
  sequenceHistoryAuditCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'audit'),
  sequenceHistoryAuditDoc: (uid: string, sequenceId: string, auditId: string) => doc(db, 'users', uid, 'user_history', 'sequence_history', 'sequences', sequenceId, 'audit', auditId),
  // Legacy/alternate transaction mirror path per spec text variants (without sequence_history segment)
  legacySequenceDoc: (uid: string, sequenceId: string) => doc(db, 'users', uid, 'sequence_history', sequenceId),
  legacySequenceTransactionsCol: (uid: string, sequenceId: string) => collection(db, 'users', uid, 'sequence_history', sequenceId, 'transactions'),
  legacySequenceTransactionDoc: (uid: string, sequenceId: string, txId: string) => doc(db, 'users', uid, 'sequence_history', sequenceId, 'transactions', txId),
  // Extent summaries (admin and user)
  adminExtentSummariesDoc: () => doc(db, 'extent_summaries', 'extent_summaries'),
  userExtentSummariesDoc: (uid: string) => doc(db, 'users', uid, 'extent_summaries', 'extent_summaries'),
  sequencesCol: (uid: string) => collection(db, 'users', uid, 'sequences'),
  sequenceDoc: (uid: string, sequenceId: string) => doc(db, 'users', uid, 'sequences', sequenceId),
  importsCol: (uid: string) => collection(db, 'users', uid, 'imports')
};

// Guard helper to ensure callers pass the current uid for sensitive paths
export function assertCurrentUid(targetUid: string, context: string): void {
  try {
    const current = getAuth().currentUser?.uid || null;
    if (!current || current !== targetUid) {
      try { logCrossUidAttempt(current, targetUid, context); } catch {}
      throw new Error(`Cross-UID access blocked in path builder: ${context}`);
    }
  } catch {}
}

export const q = {
  // Helper to build paginated queries with optional cursor
  paginated: (colRef: ReturnType<typeof collection>, orderField: string, opts?: PaginationOptions) => {
    const parts: any[] = [orderBy(orderField, 'desc')];
    if (opts?.limit) parts.push(fbLimit(opts.limit));
    const base = query(colRef, ...parts);
    if (opts?.startAfter) return query(base, fbStartAfter(opts.startAfter));
    return base;
  },
  whereEq: (colRef: ReturnType<typeof collection>, field: string, value: any) => query(colRef, where(field, '==', value as WhereFilterOp)),
};

export const ts = {
  serverNow: () => serverTimestamp(),
  fromMillis: (ms: number) => Timestamp.fromMillis(ms),
};

export type WriteOptions = {
  debounceKey?: string;
  debounceMs?: number; // default 600ms
  flush?: boolean; // flush pending
};

export async function debouncedSetDoc(ref: ReturnType<typeof doc>, data: any, options?: WriteOptions): Promise<void> {
  const key = options?.debounceKey || ref.path;
  const delay = typeof options?.debounceMs === 'number' ? options!.debounceMs! : 600; // admin-style 400–800ms default
  if (options?.flush) return debounceManager.flush(key);
  // Settles with the ACTUAL write outcome (INCIDENT-20260822) — awaiting
  // callers now see the Firestore rejection instead of a phantom success.
  return debounceManager.schedule(key, () => setDoc(ref, data, { merge: true }), delay);
}

export async function debouncedUpdateDoc(ref: ReturnType<typeof doc>, data: any, options?: WriteOptions): Promise<void> {
  const key = options?.debounceKey || ref.path;
  const delay = typeof options?.debounceMs === 'number' ? options!.debounceMs! : 600;
  if (options?.flush) return debounceManager.flush(key);
  return debounceManager.schedule(key, () => updateDoc(ref, data), delay);
}

export { addDoc, collection, deleteDoc, doc, getDoc, getDocs, setDoc };
export { fbLimit as limit, fbStartAfter as startAfter, orderBy, query, serverTimestamp, updateDoc };
export { db };

// Transactional update with conflict detection on a lastupdated field
export async function updateWithConflictCheck(
  ref: ReturnType<typeof doc>,
  lastUpdatedField: string,
  update: Record<string, any>,
  clientKnownServerTs?: any
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data() as any) : {};
    const serverTs = current?.[lastUpdatedField];
    if (clientKnownServerTs && serverTs?.toMillis && clientKnownServerTs?.toMillis && serverTs.toMillis() > clientKnownServerTs.toMillis()) {
      throw new ConflictError('Server document is newer than client-known state');
    }
    tx.set(ref, { ...update, [lastUpdatedField]: serverTimestamp() }, { merge: true });
  });
}

// Debounced update with conflict detection and backoff; debounce key scoped by doc path (per-user isolation)
export async function debouncedUpdateWithConflict(
  ref: ReturnType<typeof doc>,
  lastUpdatedField: string,
  update: Record<string, any>,
  clientKnownServerTs?: any,
  options?: WriteOptions
): Promise<void> {
  const key = options?.debounceKey || ref.path;
  const delay = typeof options?.debounceMs === 'number' ? options!.debounceMs! : 600;
  if (options?.flush) return debounceManager.flush(key);
  debounceManager.schedule(key, () => updateWithConflictCheck(ref, lastUpdatedField, update, clientKnownServerTs), delay);
}


